import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import AACChromeAPI from '@amzn/aac-chrome-api';
import { ErrorIdProvider } from '@amzn/storm-ui';
import { markComponentStart } from 'src/commons/utils/logWithTakt';
import { CHROME_APP_NAME, Components } from 'src/commons/constants';
import { performanceMark } from 'src/utils/performance-helper';
import { store } from 'src/app/store';
import { InternalChromeConfigContextProvider } from 'src/providers/InternalChromeConfigContextProvider';
import { ChromeConfigContextProvider } from 'src/providers/ChromeConfigContextProvider';
import { ConnectedChromeApp } from 'src/commons/components/ChromeApp/ChromeApp';
import {
  MarketplaceSwitcher as MarketplaceSwitcherInterface,
  updateMarketplaceSwitcher,
} from 'src/app/slice';
import { isFromBtswWc, isFromTrim } from 'src/commons/utils';
import { TrimMarketplaceSwitcher } from 'src/features/header/components/TrimMarketplaceSwitcher/TrimMarketplaceSwitcher';
import { isMobileForTrim } from 'src/commons/utils/helpers';
import { toPlainData } from 'src/commons/utils/toPlainData';
import { HeaderBundleProvider } from 'src/providers/PantherLocalizationProvider';

const MARKETPLACE_SWITCHER_CONFIG = 'initChromeMarketplaceSwitcherConfig';

export default class AXMarketplaceSwitcher extends HTMLElement {
  static observedAttributes: string[] = [MARKETPLACE_SWITCHER_CONFIG];
  _initialStyleAdded = false;

  set initChromeMarketplaceSwitcherConfig(
    initChromeMarketplaceSwitcherConfig: MarketplaceSwitcherInterface,
  ) {
    if (
      initChromeMarketplaceSwitcherConfig &&
      Object.keys(initChromeMarketplaceSwitcherConfig).length > 0
    ) {
      if (isFromTrim() && !this._initialStyleAdded) {
        this.addInitialStyle(initChromeMarketplaceSwitcherConfig);
      }
      this.updateStoreAndChromeAPI(initChromeMarketplaceSwitcherConfig);
    }
  }

  addInitialStyle(
    initChromeMarketplaceSwitcherConfig: MarketplaceSwitcherInterface,
  ): void {
    const isMobile = isMobileForTrim();
    if (initChromeMarketplaceSwitcherConfig?.useMarketplaceSwitcher) {
      const styleTag = document.createElement('style');
      styleTag.innerHTML = `
          ax-chrome-marketplace-switcher {
            min-width: 1px;
            min-height: ${isMobile ? 61 : 49}px;
            display: block;
          }
        `;
      document.head.appendChild(styleTag);
      this._initialStyleAdded = true;
    }
  }

  updateStoreAndChromeAPI(
    initChromeMarketplaceSwitcherConfig: MarketplaceSwitcherInterface,
  ): void {
    store.dispatch(
      updateMarketplaceSwitcher(
        toPlainData<MarketplaceSwitcherInterface>(
          initChromeMarketplaceSwitcherConfig,
        ),
      ),
    );
    const chromeConfig = AACChromeAPI.getChromeConfiguration();
    chromeConfig.setMarketplaceSwitcher(initChromeMarketplaceSwitcherConfig);
    AACChromeAPI.setChromeConfiguration(chromeConfig);
  }

  _renderMarketplaceSwitcher(): void {
    if (!isFromTrim() && !isFromBtswWc()) return;
    markComponentStart(Components.MARKETPLACE_SWITCHER);
    const endMarker = performanceMark('aac-chrome-ui-marketplace-switcher');

    ReactDOM.render(
      <ErrorIdProvider id="Chrome">
        <Provider store={store}>
          <InternalChromeConfigContextProvider>
            <ChromeConfigContextProvider>
              <HeaderBundleProvider>
                <ConnectedChromeApp name={CHROME_APP_NAME.MarketplaceSwitcher}>
                  <TrimMarketplaceSwitcher />
                </ConnectedChromeApp>
              </HeaderBundleProvider>
            </ChromeConfigContextProvider>
          </InternalChromeConfigContextProvider>
        </Provider>
      </ErrorIdProvider>,
      this,
    );

    endMarker();
  }

  connectedCallback(): void {
    this._renderMarketplaceSwitcher();
  }
}

if (!customElements.get('ax-chrome-marketplace-switcher')) {
  customElements.define(
    'ax-chrome-marketplace-switcher',
    AXMarketplaceSwitcher,
  );
}
